import { useI18n } from "@/lib/i18n/useI18n";
import useRequest from "@/lib/request/useRequest";
import { useTranslation } from "react-i18next";
import { object, number, string, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useMemo, useCallback } from "react";
import { FertilizerProduct, FertilizerType } from "@/types/products";
import { ApplicationMethod } from "@/types/enums";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
} from "@heroui/react";
import SelectInput from "../inputs/SelectInput";
import SectionedAutoCompleteInput from "../inputs/SectionedAutoComplete";
import NumberInput from "../inputs/NumberInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEquals, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { SimulationType } from "@/types/nitrogenPlan";
import { getSimulationTypeDisplayName } from "@/types/nitrogenPlan";
import { FieldInsights } from "@/types/fieldInsights";

const scema = object().shape({
    rateModifier: string().optional().oneOf(["=", "+", "-"]),
    editRate: number().min(0).optional(),
    fertilizerProduct: number().optional(),
    applicationMethod: string()
        .optional()
        .oneOf(Object.values(ApplicationMethod)),
    simulationModel: string().optional().oneOf(Object.values(SimulationType)),
});

type FormType = InferType<typeof scema>;

function BulkEditNitrogenPlan({
    action,
    fieldsWithPlan,
    onFinish,
}: {
    action: "edit-rate" | "set-product" | "set-method" | "set-model";
    fieldsWithPlan: FieldInsights[];
    onFinish: () => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const { t } = useTranslation();
    const { formatUnit } = useI18n();

    const form = useForm<FormType>({
        resolver: yupResolver(scema),
        defaultValues: {
            rateModifier: action === "edit-rate" ? "=" : undefined,
            editRate: undefined,
            fertilizerProduct: undefined,
            applicationMethod: undefined,
            simulationModel: SimulationType.SENTINEL,
        },
    });

    const getFertilizers = useGetRequest<FertilizerProduct[]>({
        url: "/fertilizers/",
        enabled: action === "set-product",
    });

    const fertilizerOptions = useMemo(() => {
        const dryFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Dry,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const liquidFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Liquid,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        return [
            {
                value: FertilizerType.Dry,
                label: t("Dry Fertilizers"),
                options: dryFertilizers,
            },
            {
                value: FertilizerType.Liquid,
                label: t("Liquid Fertilizers"),
                options: liquidFertilizers,
            },
        ];
    }, [getFertilizers.data, t]);

    const methodOptions = useMemo(() => {
        const types = [
            {
                id: ApplicationMethod.InjectedKnifedCoultered,
                description: "Injected/Knifed/Coultered",
            },
            { id: ApplicationMethod.Dribble, description: "Dribble" },
            { id: ApplicationMethod.Broadcast, description: "Broadcast" },
            { id: ApplicationMethod.Topdress, description: "Topdress" },
            { id: ApplicationMethod.YDrop, description: "Y-Drop" },
            {
                id: ApplicationMethod.BroadcastTillage,
                description: "Broadcast Tillage",
            },
            { id: ApplicationMethod.TwoXTwo, description: "2x2" },
            { id: ApplicationMethod.Furrow, description: "Furrow" },
            { id: ApplicationMethod.Fertigation, description: "Fertigation" },
        ];
        return sortArrayByKey(types, "description");
    }, []);

    const simulationModelOptions = useMemo(() => {
        const types = Object.values(SimulationType)
            .filter((type) => !type.startsWith("year_"))
            .map((type) => ({
                id: type,
                description: getSimulationTypeDisplayName(type),
            }));
        return sortArrayByKey(types, "description");
    }, []);

    const mutateFieldDefaults = useMutateRequest({
        method: "patch",
        url: "/fields/bulk_update/",
        successMessage: "Fields updated successfully.",
        errorMessage: "Failed to update fields.",
    });

    const handleSubmit = useCallback(
        (data: FormType) => {
            if (action === "set-product") {
                if (!data.fertilizerProduct) return;
                mutateFieldDefaults.mutate(
                    {
                        data: fieldsWithPlan.map((field) => ({
                            id: field.id,
                            default_product: data.fertilizerProduct,
                        })),
                    },
                    {
                        onSuccess: () => {
                            onFinish();
                        },
                    },
                );
            } else if (action === "set-model") {
                if (!data.simulationModel) return;
                mutateFieldDefaults.mutate(
                    {
                        data: fieldsWithPlan.map((field) => ({
                            id: field.id,
                            default_nitrogen_plan_type: data.simulationModel,
                        })),
                    },
                    {
                        onSuccess: () => {
                            onFinish();
                        },
                    },
                );
            }
        },
        [fieldsWithPlan, action, mutateFieldDefaults, onFinish],
    );

    return (
        <Modal isOpen onClose={onFinish}>
            <ModalContent>
                <FormProvider {...form}>
                    <form>
                        <ModalHeader>Edit Nitrogen Plans</ModalHeader>
                        <ModalBody>
                            {action === "edit-rate" && (
                                <>
                                    <label className="mb-2 block text-sm font-medium text-gray-700">
                                        Edit Nitrogen Rate
                                    </label>
                                    <div className="flex flex-row gap-4">
                                        <SelectInput
                                            name="rateModifier"
                                            options={[
                                                {
                                                    id: "=",
                                                    description: "",
                                                    startContent: (
                                                        <FontAwesomeIcon
                                                            icon={faEquals}
                                                            className="text-primary-500"
                                                        />
                                                    ),
                                                },
                                                {
                                                    id: "+",
                                                    description: "",
                                                    startContent: (
                                                        <FontAwesomeIcon
                                                            icon={faPlus}
                                                            className="text-success-500"
                                                        />
                                                    ),
                                                },
                                                {
                                                    id: "-",
                                                    description: "",
                                                    startContent: (
                                                        <FontAwesomeIcon
                                                            icon={faMinus}
                                                            className="text-danger-500"
                                                        />
                                                    ),
                                                },
                                            ]}
                                            isRequired
                                            className="max-w-[80px]"
                                        />
                                        <NumberInput
                                            name="editRate"
                                            endContent={
                                                <span className="text-sm">
                                                    {formatUnit(
                                                        "lbs_ac",
                                                        "kg_ha",
                                                    )}
                                                </span>
                                            }
                                            isRequired
                                        />
                                    </div>
                                </>
                            )}
                            {action === "set-product" && (
                                <SectionedAutoCompleteInput
                                    name="fertilizerProduct"
                                    label="Fertilizer Product"
                                    labelPlacement="outside"
                                    groups={fertilizerOptions}
                                    placeholder="Select Fertilizer Product"
                                    required
                                />
                            )}
                            {action === "set-method" && (
                                <SelectInput
                                    name="applicationMethod"
                                    label="Application Method"
                                    labelPlacement="outside"
                                    options={methodOptions}
                                    placeholder="Select Application Method"
                                    isRequired
                                />
                            )}
                            {action === "set-model" && (
                                <SelectInput
                                    name="simulationModel"
                                    label="Simulation Model"
                                    labelPlacement="outside"
                                    options={simulationModelOptions}
                                    placeholder="Select Model"
                                    isRequired
                                />
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="primary"
                                onPress={() => {
                                    form.trigger();
                                    form.handleSubmit(handleSubmit)();
                                }}
                                isLoading={mutateFieldDefaults.isPending}
                                fullWidth
                            >
                                Update Nitrogen Plans
                            </Button>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}

export default BulkEditNitrogenPlan;
